import { useCallback, useEffect, useState } from 'react';
import { BrandingStoreContext } from './brandingStore';

const BRANDING_STORAGE_KEY = 'adaptive-lms-branding';

const DEFAULT_BRANDING = {
  appName: 'Adaptive Learning',
  logoUrl: '',
  colors: {
    primary: '#0d6efd',
    secondary: '#6c757d',
    success: '#198754',
    danger: '#dc3545',
    warning: '#ffc107',
    info: '#0dcaf0',
  },
};

function hexToRgb(hex) {
  if (!hex || typeof hex !== 'string') return null;
  const normalized = hex.replace('#', '').trim();
  if (!/^[0-9a-fA-F]{6}$/.test(normalized)) return null;
  const value = parseInt(normalized, 16);
  return {
    r: (value >> 16) & 255,
    g: (value >> 8) & 255,
    b: value & 255,
  };
}

function applyBranding(branding) {
  if (typeof document === 'undefined') return;

  const root = document.documentElement;
  const colors = branding?.colors || DEFAULT_BRANDING.colors;

  const applyColor = (key, cssVar, bsVar) => {
    const color = colors[key] || DEFAULT_BRANDING.colors[key];
    root.style.setProperty(cssVar, color);
    root.style.setProperty(bsVar, color);

    const rgb = hexToRgb(color);
    if (rgb) {
      root.style.setProperty(`${bsVar}-rgb`, `${rgb.r}, ${rgb.g}, ${rgb.b}`);
    }
  };

  applyColor('primary', '--primary-color', '--bs-primary');
  applyColor('secondary', '--secondary-color', '--bs-secondary');
  applyColor('success', '--success-color', '--bs-success');
  applyColor('danger', '--danger-color', '--bs-danger');
  applyColor('warning', '--warning-color', '--bs-warning');
  applyColor('info', '--info-color', '--bs-info');

  if (branding?.appName) {
    document.title = branding.appName;
  }
}

function loadBranding() {
  if (typeof window === 'undefined') {
    return DEFAULT_BRANDING;
  }

  try {
    const raw = window.localStorage.getItem(BRANDING_STORAGE_KEY);
    if (!raw) {
      return DEFAULT_BRANDING;
    }

    const parsed = JSON.parse(raw);
    return {
      ...DEFAULT_BRANDING,
      ...parsed,
      colors: {
        ...DEFAULT_BRANDING.colors,
        ...(parsed?.colors || {}),
      },
    };
  } catch {
    return DEFAULT_BRANDING;
  }
}

function persistBranding(branding) {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.setItem(BRANDING_STORAGE_KEY, JSON.stringify(branding));
}

export function BrandingProvider({ children }) {
  const [branding, setBranding] = useState(DEFAULT_BRANDING);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const stored = loadBranding();
    setBranding(stored);
    applyBranding(stored);
    setLoading(false);
  }, []);

  const updateBranding = useCallback((updates) => {
    setBranding(prev => {
      const next = {
        ...prev,
        ...updates,
        colors: {
          ...prev.colors,
          ...(updates?.colors || {}),
        },
      };

      persistBranding(next);
      applyBranding(next);
      return next;
    });
  }, []);

  const resetBranding = useCallback(() => {
    setBranding(DEFAULT_BRANDING);
    persistBranding(DEFAULT_BRANDING);
    applyBranding(DEFAULT_BRANDING);
  }, []);

  return (
    <BrandingStoreContext.Provider value={{ branding, loading, updateBranding, resetBranding }}>
      {children}
    </BrandingStoreContext.Provider>
  );
}
